import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Chip,
  Divider,
  alpha,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { LAYOUT_CONFIG, MENU_ITEMS } from '../constants/layoutConstants';
import usePermissions from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';

const DesktopSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openItems, setOpenItems] = useState({});
  const { hasModule } = usePermissions();
  const { clearToken } = useAuth();

  const filterMenuItems = (items) =>
    items.reduce((filtered, item) => {
      if (item.isVersionTag) {
        filtered.push(item);
        return filtered;
      }

      const hasPath = Boolean(item.path);
      const filteredChildren = item.children ? filterMenuItems(item.children) : [];
      const hasVisibleChildren = filteredChildren.length > 0;
      const canAccessItem = !item.requiredModule || hasModule(item.requiredModule);

      if (item.children && !hasPath) {
        if (hasVisibleChildren) {
          filtered.push({
            ...item,
            children: filteredChildren,
          });
        }
        return filtered;
      }

      if (!canAccessItem) return filtered;

      if (item.children) {
        if (!hasVisibleChildren) return filtered;

        filtered.push({
          ...item,
          children: filteredChildren,
        });
        return filtered;
      }

      filtered.push(item);
      return filtered;
    }, []);

  const filteredMenuItems = filterMenuItems(MENU_ITEMS);

  const isItemActive = (path) => {
    return location.pathname === path;
  };

  const isParentActive = (item) => {
    if (item.path && isItemActive(item.path)) return true;
    if (item.children) {
      return item.children.some((child) => {
        if (child.path && isItemActive(child.path)) return true;
        if (child.children) {
          return child.children.some(
            (grandchild) => grandchild.path && isItemActive(grandchild.path)
          );
        }
        return false;
      });
    }
    return false;
  };

  const handleItemAction = (item) => {
    if (item?.isLogout) {
      clearToken();
      navigate('/');
      return;
    }

    if (item?.path) {
      navigate(item.path);
    }
  };

  const handleSubmenuToggle = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderMenuItem = (item, index, level = 0, parentKey = '') => {
    const hasChildren = item.children && item.children.length > 0;
    const itemKey = parentKey ? `${parentKey}-${index}` : `${index}`;
    const isOpen = openItems[itemKey];
    const isActive = isParentActive(item);

    if (item.isVersionTag) {
      return (
        <Box key={itemKey} sx={{ p: 2, textAlign: 'center' }}>
          <Chip
            label={item.label}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 'bold',
            }}
          />
        </Box>
      );
    }

    const isLogout = Boolean(item.isLogout);

    return (
      <Box key={itemKey}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                handleSubmenuToggle(itemKey);
              } else {
                handleItemAction(item);
              }
            }}
            sx={{
              pl: 2 + level * 2,
              py: 1.5,
              minHeight: 48,
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              bgcolor: isActive
                ? (theme) => alpha(theme.palette.primary.main, 0.1)
                : 'transparent',
              color: isLogout
                ? 'error.main'
                : isActive
                ? 'primary.main'
                : 'text.primary',
              '&:hover': isLogout
                ? {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    color: 'error.main',
                  }
                : {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isLogout
                    ? 'error.main'
                    : isActive
                    ? 'primary.main'
                    : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}

            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: level === 0 ? '0.95rem' : '0.875rem',
                fontWeight: level === 0 ? 600 : 500,
                color: isLogout
                  ? 'error.main'
                  : isActive
                  ? 'primary.main'
                  : 'inherit',
              }}
            />

            {hasChildren && (
              <Box sx={{ color: 'text.secondary' }}>{isOpen ? <ExpandLess /> : <ExpandMore />}</Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child, childIndex) =>
                renderMenuItem(child, childIndex, level + 1, itemKey)
              )}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: LAYOUT_CONFIG.desktop.sidebarWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: LAYOUT_CONFIG.desktop.sidebarWidth,
          boxSizing: 'border-box',
          mt: `${LAYOUT_CONFIG.desktop.headerHeight}px`,
          height: `calc(100vh - ${LAYOUT_CONFIG.desktop.headerHeight}px)`,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'background.default',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'action.disabled',
            borderRadius: '3px',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          },
        },
      }}
    >
      <Box sx={{ py: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{textAlign: 'center'}}>
          <Typography
            variant="overline"
            sx={{
              px: 3,
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            Menú Principal
          </Typography>
          <Divider sx={{  mb: 2 }} />
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          <List disablePadding>{filteredMenuItems.map((item, index) => renderMenuItem(item, index))}</List>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DesktopSidebar;
